import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff, KeyRound } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login, register, forgot, reset
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "", password: "", name: "",
    referral_code: localStorage.getItem("savecation_referral_code") || "",
    resetCode: "", newPassword: "",
  });

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name, referral_code: formData.referral_code };

      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Authentication failed");

      localStorage.removeItem("savecation_referral_code");
      toast.success(mode === "login" ? "Welcome back!" : "Account created! Check your email for verification code.");
      if (data.onboarding_completed) navigate("/dashboard");
      else navigate("/onboarding", { state: { user: data } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();
      toast.success("Reset code sent! Check your email.");
      setMode("reset");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, code: formData.resetCode, new_password: formData.newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success("Password reset! Please log in.");
      setMode("login");
      setFormData({ ...formData, password: "", resetCode: "", newPassword: "" });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const titles = { login: "Welcome Back", register: "Create Account", forgot: "Forgot Password", reset: "Reset Password" };
  const subtitles = { login: "Sign in to continue your journey", register: "Start saving for your dream trip", forgot: "Enter your email to receive a reset code", reset: "Enter the code from your email" };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex flex-col" data-testid="auth-page">
      <header className="p-4">
        <button onClick={() => mode === "login" || mode === "register" ? navigate("/") : setMode("login")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-5 h-5" /> {mode === "forgot" || mode === "reset" ? "Back to login" : "Back"}
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900" data-testid="auth-title">{titles[mode]}</h1>
            <p className="text-slate-500 mt-2">{subtitles[mode]}</p>
          </div>

          {/* Google Login (login/register only) */}
          {(mode === "login" || mode === "register") && (
            <>
              <Button onClick={handleGoogleLogin} variant="outline" className="w-full h-12 mb-6 gap-3 text-base" data-testid="google-login-btn">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-b from-sky-50 to-white text-slate-500">or continue with email</span>
                </div>
              </div>
            </>
          )}

          {/* Login / Register Form */}
          {(mode === "login" || mode === "register") && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="pl-10 h-12" required={mode === "register"} data-testid="name-input" />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="pl-10 h-12" required data-testid="email-input" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type={showPassword ? "text" : "password"} placeholder="Password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="pl-10 pr-10 h-12" required minLength={8} data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {mode === "register" && formData.referral_code && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                  Referral code applied: <strong>{formData.referral_code}</strong> — You'll get $5 bonus!
                </div>
              )}
              {mode === "login" && (
                <button type="button" onClick={() => setMode("forgot")} className="text-sm text-sky-600 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              )}
              <Button type="submit" disabled={isLoading} className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-base" data-testid="signin-btn">
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {mode === "login" ? "Signing in..." : "Creating account..."}
                  </span>
                ) : mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="email" placeholder="Your email address" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="pl-10 h-12" required data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={isLoading} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-base" data-testid="send-reset-btn">
                {isLoading ? "Sending..." : "Send Reset Code"}
              </Button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="text" placeholder="6-digit code from email" value={formData.resetCode} onChange={(e) => setFormData({ ...formData, resetCode: e.target.value })} className="pl-10 h-12 tracking-widest text-center font-bold" required data-testid="reset-code-input" maxLength={6} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type={showPassword ? "text" : "password"} placeholder="New password (min 8 chars)" value={formData.newPassword} onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })} className="pl-10 pr-10 h-12" required minLength={8} data-testid="new-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <Button type="submit" disabled={isLoading} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-base" data-testid="reset-password-btn">
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
              <p className="text-center text-sm text-slate-500">
                Didn't get the code? <button type="button" onClick={() => setMode("forgot")} className="text-sky-600 hover:underline">Send again</button>
              </p>
            </form>
          )}

          {/* Toggle login/register */}
          {(mode === "login" || mode === "register") && (
            <p className="text-center text-slate-500 mt-6">
              {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sky-600 hover:underline font-medium" data-testid="toggle-auth-mode">
                {mode === "login" ? "Sign Up" : "Sign In"}
              </button>
            </p>
          )}

          <p className="text-center text-xs text-slate-400 mt-8">
            By continuing, you agree to our{" "}
            <a href="/terms" className="hover:underline">Terms of Service</a> and{" "}
            <a href="/privacy" className="hover:underline">Privacy Policy</a>
          </p>
        </div>
      </main>
    </div>
  );
}
